import React, { Component } from "react";
import axios from "axios";
import {
  Card,
  Button,
  Table,
  Spinner,
  Alert,
  Row,
  Col,
  Badge,
} from "react-bootstrap";

/**
 * Props:
 * - apiBaseUrl?: string
 * - storeId: string
 * - fromDate: string | Date
 * - toDate: string | Date
 * - reconciliationId?: string
 * - currency?: string              // <— used for totals (default INR)
 * - onLoaded?: (entries: any[]) => void
 * - selectedIds?: Set<string>|string[]
 * - onToggle?: (id: string) => void
 * - isRowMatched?: (id: string) => boolean
 * - loadPath?: string
 * - usePost?: boolean
 * - initialEntries?: any[]
 */
export default class LedgerEntryList extends Component {
  state = {
    loading: false,
    error: "",
    entries: [],
    hydratedFromProps: false,
  };

  abortController = null;

  dateKey = (x) => {
    const v =
      x.date ??
      this.pick(x, "TxnDate", "txnDate") ??
      this.pick(x, "Date", "date") ??
      "";
    const t = Date.parse(v);
    return Number.isFinite(t) ? t : 0;
  };

  componentDidMount() {
    const { initialEntries } = this.props;
    if (Array.isArray(initialEntries) && initialEntries.length > 0) {
      const entries = this.normalize(initialEntries).sort(
        (a, b) =>
          this.dateKey(a) - this.dateKey(b) ||
          String(a.particulars || "").localeCompare(String(b.particulars || ""))
      );
      this.setState({ entries, hydratedFromProps: true, error: "" });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.initialEntries !== prevProps.initialEntries) {
      const rows = Array.isArray(this.props.initialEntries)
        ? this.props.initialEntries
        : [];
      const entries = this.normalize(rows).sort(
        (a, b) =>
          this.dateKey(a) - this.dateKey(b) ||
          String(a.particulars || "").localeCompare(String(b.particulars || ""))
      );
      this.setState({ entries, hydratedFromProps: rows.length > 0, error: "" });
    }
  }

  componentWillUnmount() {
    if (this.abortController) this.abortController.abort();
  }

  // ---- URL helpers ----------------------------------------------------------
  getApiBase = () => {
    let base =
      this.props.apiBaseUrl ??
      process.env.REACT_APP_API ??
      (typeof window !== "undefined" ? window.location.origin + "/" : "/");
    if (!base.endsWith("/")) base += "/";
    if (!/\/api\/$/i.test(base)) {
      if (/\/api$/i.test(base)) base += "/";
      else base += "api/";
    }
    return base;
  };

  // ---- formatting helpers ---------------------------------------------------
  toIsoLocal = (d) => {
    if (!d) return "";
    const dt = d instanceof Date ? d : new Date(d);
    const pad = (n) => String(n).padStart(2, "0");
    return (
      dt.getFullYear() +
      "-" +
      pad(dt.getMonth() + 1) +
      "-" +
      pad(dt.getDate()) +
      "T" +
      pad(dt.getHours()) +
      ":" +
      pad(dt.getMinutes()) +
      ":" +
      pad(dt.getSeconds())
    );
  };

  formatDate = (d) => {
    if (!d) return "";
    const dt = d instanceof Date ? d : new Date(d);
    const pad = (n) => String(n).padStart(2, "0");
    const day = pad(dt.getDate());
    const month = pad(dt.getMonth() + 1);
    const year = String(dt.getFullYear()).slice(-2);
    const hours = pad(dt.getHours());
    const mins = pad(dt.getMinutes());
    return `${day}-${month}-${year} ${hours}:${mins}`;
  };

  formatMoney = (amt) => {
    const cur = this.props.currency || "INR";
    const num = typeof amt === "number" ? amt : Number(amt || 0);
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: cur,
        maximumFractionDigits: 2,
      }).format(num);
    } catch {
      return num.toFixed(2);
    }
  };

  pick = (obj, pascal, camel) => obj?.[pascal] ?? obj?.[camel];

  normalize = (rows) => {
    if (!Array.isArray(rows)) return [];
    return rows.map((e, i) => {
      const id = e.Id || e.id || e.ID || String(i);
      const date =
        this.pick(e, "TxnDate", "txnDate") ?? this.pick(e, "Date", "date");
      const particulars =
        this.pick(e, "Particulars", "particulars") ??
        this.pick(e, "Narration", "narration");
      const amount =
        this.pick(e, "Amount", "amount") ?? this.pick(e, "Value", "value") ?? 0;
      const debitCredit =
        this.pick(e, "DebitCredit", "debitCredit") ??
        this.pick(e, "CreditOrDebit", "creditOrDebit") ??
        "";
      return { id, date, particulars, amount, debitCredit };
    });
  };

  handleLoad = async () => {
    const { storeId, fromDate, toDate, onLoaded, reconciliationId } =
      this.props;

    if (!storeId)
      return this.setState({ error: "StoreId is required.", entries: [] });
    if (!fromDate || !toDate)
      return this.setState({
        error: "From/To dates are required.",
        entries: [],
      });

    this.setState({ loading: true, error: "" });
    try {
      const base = this.getApiBase();
      const url =
        this.props.loadPath ??
        `${base}LedgerEntry/Load/${encodeURIComponent(storeId)}`;

      const params = {
        from: this.toIsoLocal(fromDate),
        to: this.toIsoLocal(toDate),
      };
      if (reconciliationId) params.reconciliationId = reconciliationId;

      const { data } = await axios.get(url, { params });
      const entries = this.normalize(Array.isArray(data) ? data : []).sort(
        (a, b) =>
          this.dateKey(a) - this.dateKey(b) ||
          String(a.particulars || "").localeCompare(String(b.particulars || ""))
      );
      this.setState({ entries, loading: false, hydratedFromProps: false });
      onLoaded && onLoaded(entries);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        "Failed to load ledger entries.";
      this.setState({ error: msg, loading: false, entries: [] });
    }
  };

  isSelected = (id) => {
    const { selectedIds } = this.props;
    if (!selectedIds) return false;
    if (selectedIds instanceof Set) return selectedIds.has(id);
    return Array.isArray(selectedIds) ? selectedIds.includes(id) : false;
  };

  // ---- CSV export (unchanged) ----------------------------------------------
  csvEscape = (val) => {
    if (val === null || val === undefined) return "";
    const s = String(val);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  buildCsv = (rows) => {
    const header = ["Date", "Particulars", "Amount", "CreditOrDebit"];
    const lines = [header.map(this.csvEscape).join(",")];

    rows.forEach((e, idx) => {
      const date =
        e.date ??
        this.pick(e, "TxnDate", "txnDate") ??
        this.pick(e, "Date", "date");
      const particulars =
        e.particulars ?? this.pick(e, "Particulars", "particulars");
      const amount = e.amount ?? this.pick(e, "Amount", "amount");
      const cd = (
        e.debitCredit ??
        this.pick(e, "DebitCredit", "debitCredit") ??
        ""
      ).toString();

      const isoLocal = date ? this.toIsoLocal(date) : "";
      const row = [isoLocal, particulars, amount, cd];
      lines.push(row.map(this.csvEscape).join(","));
    });

    return "\ufeff" + lines.join("\r\n");
  };

  handleDownloadCsv = () => {
    const { entries } = this.state;
    if (!entries || entries.length === 0) return;

    const { storeId, fromDate, toDate, selectedIds } = this.props;
    let rows = entries;

    const selCount =
      selectedIds instanceof Set
        ? selectedIds.size
        : Array.isArray(selectedIds)
        ? selectedIds.length
        : 0;

    if (selCount > 0) {
      const ids =
        selectedIds instanceof Set ? selectedIds : new Set(selectedIds);
      rows = entries.filter((e, i) => {
        const id = e.id || e.Id || e.ID || String(i);
        return ids.has(id);
      });
    }

    const csv = this.buildCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const safe = (s) => (s ? String(s).replace(/[^a-z0-9-_]+/gi, "_") : "");
    const fname = `ledger_entries_${safe(storeId)}_${safe(
      this.toIsoLocal(fromDate)
    )}_${safe(this.toIsoLocal(toDate))}.csv`;

    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ---- render ---------------------------------------------------------------
  render() {
    const { entries, loading, error, hydratedFromProps } = this.state;
    const { onToggle, isRowMatched } = this.props;

    // Totals: Credit / Debit / Net
    const totals = entries.reduce(
      (acc, e) => {
        const amt = Math.abs(
          Number(e.amount ?? this.pick(e, "Amount", "amount") ?? 0)
        );
        const side = (
          e.debitCredit ??
          this.pick(e, "DebitCredit", "debitCredit") ??
          ""
        )
          .toString()
          .trim()
          .toLowerCase();
        if (side === "credit") acc.cr += amt;
        else if (side === "debit") acc.dr += amt;
        else acc.unk += amt;
        return acc;
      },
      { cr: 0, dr: 0, unk: 0 }
    );
    const net = totals.cr - totals.dr;

    return (
      <Card className="shadow-sm">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div className="fw-semibold d-flex align-items-center gap-2">
            <span>Ledger Entries</span>
            {hydratedFromProps && (
              <Badge bg="info" title="Loaded from reconciliation">
                preloaded
              </Badge>
            )}
          </div>
          <div className="d-flex">
            <Button
              variant="primary"
              size="sm"
              onClick={this.handleLoad}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading...
                </>
              ) : (
                "Load All System Entries"
              )}
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={this.handleDownloadCsv}
              disabled={loading || entries.length === 0}
              className="ms-2"
            >
              Download CSV
            </Button>
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          {error && (
            <Alert variant="danger" className="m-3">
              {error}
            </Alert>
          )}

          <div className="table-responsive">
            <Table striped hover responsive className="mb-0">
              <thead className="table-light text-center">
                <tr>
                  <th style={{ width: 42 }}></th>
                  <th style={{ width: 140 }}>Date</th>
                  <th>Particulars</th>
                  <th className="text-end" style={{ width: 160 }}>
                    Amount
                  </th>
                  <th style={{ width: 140 }}>CreditOrDebit</th>
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && !loading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4 text-muted">
                      No entries. Click <strong>Load</strong> to fetch from
                      system.
                    </td>
                  </tr>
                ) : (
                  entries.map((e, idx) => {
                    const id = e.id || e.Id || e.ID || String(idx);
                    const date =
                      e.date ??
                      this.pick(e, "TxnDate", "txnDate") ??
                      this.pick(e, "Date", "date");
                    const particulars =
                      e.particulars ??
                      this.pick(e, "Particulars", "particulars");
                    const amount = e.amount ?? this.pick(e, "Amount", "amount");
                    const side = (
                      e.debitCredit ??
                      this.pick(e, "DebitCredit", "debitCredit") ??
                      ""
                    )
                      .toString()
                      .trim()
                      .toLowerCase();
                    const matched =
                      typeof isRowMatched === "function"
                        ? isRowMatched(id)
                        : false;
                    const disabled = matched || !onToggle;

                    return (
                      <tr
                        key={id}
                        className={
                          matched ? "table-success text-center" : "text-center"
                        }
                      >
                        <td>
                          <input
                            type="checkbox"
                            disabled={disabled}
                            checked={this.isSelected(id)}
                            onChange={() => onToggle && onToggle(id)}
                          />
                        </td>
                        <td>{this.formatDate(date)}</td>
                        <td>{particulars}</td>
                        <td className="text-end">{this.formatMoney(amount)}</td>
                        <td>
                          {side ? (
                            <Badge
                              bg={side === "credit" ? "success" : "danger"}
                            >
                              {side.charAt(0).toUpperCase() + side.slice(1)}
                            </Badge>
                          ) : (
                            ""
                          )}
                        </td>
                        <td>
                          {matched ? <Badge bg="success">Matched</Badge> : null}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>

        <Card.Footer>
          <Row className="align-items-center">
            <Col className="text-muted">
              {entries.length} item{entries.length === 1 ? "" : "s"}
            </Col>
            <Col className="text-end">
              <span className="me-3">
                <strong>Cr:</strong> {this.formatMoney(totals.cr)}
              </span>
              <span className="me-3">
                <strong>Dr:</strong> {this.formatMoney(totals.dr)}
              </span>
              <span>
                <strong>Net:</strong> {this.formatMoney(net)}
              </span>
            </Col>
          </Row>
        </Card.Footer>
      </Card>
    );
  }
}
